import { useEffect, useRef, useState } from 'react';

/**
 * Le chrono local.
 *
 * Le serveur detient l'horloge, mais il ne parle qu'aux vrais changements
 * d'etat — cinq paliers par manche, une quinzaine de messages en tout. Si le
 * client attendait un message pour afficher une seconde, le decompte
 * avancerait par a-coups de quatre secondes.
 *
 * Il interpole donc entre deux messages, a partir de l'echeance calculee a la
 * reception. Ce n'est pas une source de verite concurrente : le prochain
 * message ecrase toujours ce que le client croyait. Il ne fait que remplir le
 * silence.
 */

/** Rafraichit `remainingMs` a chaque image tant que l'echeance n'est pas passee. */
export function useRemaining(endsAt: number | null): number {
  const [remaining, setRemaining] = useState(() =>
    endsAt === null ? 0 : Math.max(0, endsAt - performance.now()),
  );
  const frameRef = useRef(0);

  useEffect(() => {
    if (endsAt === null) {
      setRemaining(0);
      return;
    }

    const step = () => {
      const left = Math.max(0, endsAt - performance.now());
      setRemaining(left);
      // On s'arrete a zero : continuer a demander des images pour afficher
      // toujours "0" garderait l'onglet eveille pour rien.
      if (left > 0) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frameRef.current);
  }, [endsAt]);

  return remaining;
}

/**
 * Le temps en secondes entieres, pour un affichage qui ne tremble pas.
 * `Math.ceil` plutot que `Math.floor` : tant qu'il reste un souffle, le
 * compteur affiche 1, et 0 signifie vraiment fini.
 */
export function seconds(ms: number): number {
  return Math.ceil(ms / 1000);
}

/** Part de temps ECOULEE, de 0 a 1. C'est ce qu'attend une jauge qui se vide. */
export function elapsedRatio(remainingMs: number, totalMs: number | null): number {
  if (!totalMs || totalMs <= 0) return 0;
  return Math.min(1, Math.max(0, 1 - remainingMs / totalMs));
}
